import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box } from '@mui/material'
import { usePatternSidePanelContext } from '../../contexts/PatternSidePanelContext'
import { useApplicationSuiteContext } from '../../../../contexts/ApplicationSuiteProvider'
import { useHttpsCallable } from 'react-firebase-hooks/functions'
import { firebase_functions } from '../../../../common/firebaseConfig'
import usePaginatedLoader from '../../../../hooks/usePaginatedLoader'
import ReportCardList from '../../../../components/backtests/ReportCardList'
import { collection, query, where, limit } from 'firebase/firestore'
import { firebase_firestore } from '../../../../common/firebaseConfig'
import { useCollectionData } from 'react-firebase-hooks/firestore'
import { usePatternContext } from '../../contexts/PatternContext'
import { useHiddenBacktests } from '../../../../hooks/useHiddenBacktests'

const BacktestsTab: React.FC = () => {
  const { selectedPattern } = usePatternSidePanelContext()
  const [lastQueryTime, setLastQueryTime] = useState(new Date())

  const { tradingHouse, usermeta } = useApplicationSuiteContext()

  const { focusedBacktest, setFocusedBacktest } = usePatternContext()

  const hiddenBacktestIds = useHiddenBacktests(usermeta?.gcid)

  const [getShowcaseBacktests] = useHttpsCallable(firebase_functions, 'getBacktests')
  const func = useCallback(
    (params: any) =>
      getShowcaseBacktests({
        ...params,
        tradingHouse,
        type: 'user',
        backtestFilters: { patternId: selectedPattern?.id },
        sort: { field: 'startTime', direction: 'desc' },
      }),
    [getShowcaseBacktests, tradingHouse, selectedPattern]
  )

  const {
    data: backtests,
    loadMore,
    isLoading,
    totalCount,
    reset,
  } = usePaginatedLoader({
    queryFunction: func,
    take: 10,
  })

  const [runningBacktests] = useCollectionData(
    query(
      collection(firebase_firestore, `usermeta/${usermeta.gcid}/backtests`),
      where('backtestSettings.tradingHouse', '==', tradingHouse),
      where('status', 'in', ['running']),
      where('backtestSettings.patternId', '==', selectedPattern?.id || '')
    )
  )

  // Query for backtests that completed since our last query
  const [recentlyCompletedBacktests] = useCollectionData(
    query(
      collection(firebase_firestore, `usermeta/${usermeta.gcid}/backtests`),
      where('backtestSettings.tradingHouse', '==', tradingHouse),
      where('status', 'in', ['complete', 'error']),
      where('backtestSettings.patternId', '==', selectedPattern?.id || ''),
      where('endTime', '>=', lastQueryTime.getTime()),
      limit(1)
    )
  )

  // Check for new completed backtests and reset if found
  useEffect(() => {
    if (recentlyCompletedBacktests && recentlyCompletedBacktests.length > 0) {
      setLastQueryTime(new Date())
      reset()
    }
  }, [recentlyCompletedBacktests, reset])

  const runningBacktestHeaderComponent = useMemo(() => {
    return runningBacktests?.length ? (
      <Box height={150}>
        <ReportCardList
          backtests={runningBacktests}
          condensed={true}
          hiddenBacktestIds={hiddenBacktestIds}
        />
      </Box>
    ) : null
  }, [runningBacktests, hiddenBacktestIds])

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        position: 'relative',
        py: 1,
      }}
    >
      <ReportCardList
        backtests={backtests}
        loadMore={loadMore}
        isLoading={isLoading}
        totalCount={totalCount}
        emptyListText={{
          title: 'No backtests found',
          subTitle: 'Run a backtest to see how your pattern performs on historical data',
        }}
        headerComponent={runningBacktestHeaderComponent}
        activeBacktest={focusedBacktest}
        onBacktestSelection={setFocusedBacktest}
        hiddenBacktestIds={hiddenBacktestIds}
      />
    </Box>
  )
}

export default BacktestsTab
